// FEATURE_041 — public (pre-login) PUBLISHED legal terms shown by the login/signup footer
// links. Same wire shape as the BE SignupTermResponse DTO (GET /admin/auth/terms). The
// endpoint returns ALL published terms as a list; the viewer picks one by `typeId`.
export interface PublishedTerm {
  // ad_terms_version.id of the PUBLISHED version.
  termVersionId: number;
  // ad_terms_type.id — stable across versions; used to select which term the footer link opens.
  typeId: number;
  // Type's Korean name (the only shipped locale).
  name: string;
  // Whether consent is mandatory (drives the 필수/선택 badge).
  required: boolean;
  // Server-sanitized HTML body — rendered verbatim (BE owns sanitization).
  content: string;
  // Published version label (ad_terms_version.version_label, e.g. "v1.4") — shown in the
  // viewer meta line (SHCT-356). Optional until the BE adds it to GET /admin/auth/terms.
  version?: string;
  // Effective date, ISO `YYYY-MM-DD` (ad_terms_version.effective_date) — the viewer formats
  // `-`→`.`. Optional until the BE adds it to GET /admin/auth/terms (SHCT-356).
  effectiveDate?: string;
}

export type PublishedTermsErrorCode = 'NETWORK' | 'UNKNOWN';

export class PublishedTermsError extends Error {
  code: PublishedTermsErrorCode;
  constructor(code: PublishedTermsErrorCode, message: string) {
    super(message);
    this.name = 'PublishedTermsError';
    this.code = code;
  }
}

export interface PublishedTermsApi {
  listPublishedTerms(): Promise<PublishedTerm[]>;
}

// Footer link identifier (mirrors the prototype terms.html `?type=` values) → seed term
// `typeId`. The 4 default seeds are stable (deletable:false, fixed ids from BE migration V6):
//   1 서비스 이용약관 · 2 개인정보 수집 및 이용 · 3 민감정보 수집 및 이용 · 4 개인정보 처리방침.
export type TermSlug = 'service' | 'policy' | 'privacy' | 'sensitive';

export const TERM_SLUG_TO_TYPE_ID: Record<TermSlug, number> = {
  service: 1, // footer.terms — 이용약관
  privacy: 2, // footer.personalInfo — 개인정보 수집 및 이용 동의
  sensitive: 3, // footer.sensitiveInfo — 민감정보 수집 및 이용 동의
  policy: 4, // footer.privacy — 개인정보처리방침
};

export const isTermSlug = (value: string | null): value is TermSlug =>
  value === 'service' || value === 'policy' || value === 'privacy' || value === 'sensitive';
